import { FormEventHandler, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import { capitalize } from '@mui/material/utils';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useLocationStore } from '@libs/store/locations';
import { useAssignStore } from '@libs/store/talents';
import { getShiftType } from '@libs/models/shifts/shift';
import { FormAutocomplete, Form } from '@molecules/form';
import { PrimaryDialog, PrimaryDialogProps } from '@atoms/layout';
import { LoadingButton, Button } from '@atoms/buttons';
import { FaIcon } from '@atoms/icons';
import { prepareTalentsOptions } from '../helpers';
import { Box, Stack, Tooltip } from '@mui/material';
import { OrganizationAvatar } from '@atoms/dataDisplay';

type AssignDialogProps = Omit<PrimaryDialogProps, 'onSubmit'> & {
  shift: {
    shiftStart?: Date;
    shiftLength?: number;
    qualifications?: { id: string; abbreviation: string }[];
  };
  onSubmit?: (talent: string, staffingType?: string) => void;
  loading?: boolean;
  allowOnlyInternals?: boolean;
};
export function AssignDialog({ shift, onSubmit, loading, allowOnlyInternals, ...props }: AssignDialogProps) {
  return (
    <PrimaryDialog fullWidth {...props}>
      <Content
        shift={shift}
        onCloseClick={() => props.onClose?.({}, 'escapeKeyDown')}
        onSubmit={onSubmit}
        loading={loading}
        allowOnlyInternals={allowOnlyInternals}
      />
    </PrimaryDialog>
  );
}

type ContentProps = {
  shift: {
    shiftStart?: Date;
    shiftLength?: number;
    qualifications?: { id: string; abbreviation: string }[];
  };
  onCloseClick: () => void;
  onSubmit?: (talent: string, staffingType?: string) => void;
  loading?: boolean;
  allowOnlyInternals?: boolean;
};
function Content({ shift, onCloseClick, onSubmit, loading = false, allowOnlyInternals }: ContentProps) {
  const intl = useIntl();
  const shiftType = getShiftType(dayjs(shift?.shiftStart));
  const to = dayjs(shift?.shiftStart).add(shift?.shiftLength ?? 0, 'millisecond');
  const data = useAssignStore((s) => s.data);
  const loadData = useAssignStore((s) => s.loadData);
  const { currentLocationID } = useLocationStore();

  const selectedQualifications = (shift?.qualifications || []).map((q) => q.id) ?? [];
  const talentsOptions = prepareTalentsOptions(data, selectedQualifications, [], currentLocationID).filter((talent) =>
    allowOnlyInternals ? !!talent.agency == false : true,
  );

  const methods = useForm<AssignForm>({
    defaultValues: {
      talent: '',
    },
  });

  const [assigneeValue, setAssigneeValue] = useState<string>('');

  useEffect(() => {
    if (!currentLocationID) {
      return;
    }
    loadData(assigneeValue, {
      locations: { key: 'locations', value: [currentLocationID] },
      qualifications: { key: 'qualifications', value: shift.qualifications?.map((q) => q.id) ?? [] },
    });
  }, [assigneeValue, currentLocationID, loadData, shift.qualifications]);

  const handleAssigneeChange: FormEventHandler<HTMLDivElement> = (evt) => {
    const target = evt.target as HTMLInputElement;
    setAssigneeValue(target.value);
  };

  return (
    <>
      <Form
        formUtils={methods}
        onSubmit={(v) => {
          const talentInfo = talentsOptions.find((t) => t.id === v.talent);
          onSubmit?.(v.talent, talentInfo?.staffingType);
        }}
      >
        <DialogTitle sx={{ px: 3, pt: 4, pb: 2 }}>
          <Stack direction="row">
            <Box>
              <Typography variant="h5">
                <FormattedMessage id="shifts.assignDialog.title" />
              </Typography>
            </Box>
            <Box flex={1} />
            <Box>
              <IconButton sx={{ width: '40px', position: 'relative', top: -5, right: -5 }} onClick={onCloseClick}>
                <FaIcon name="xmark" variant="light" />
              </IconButton>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" noWrap>
            <FormattedMessage
              id="shifts.assignDialog.shiftDetails"
              values={{
                period: `${intl.formatTime(shift?.shiftStart, { hour: 'numeric' })} - ${intl.formatTime(to.toDate(), { hour: 'numeric' })}`,
                length: intl.formatMessage(
                  { id: 'shifts.length' },
                  {
                    length: Math.round((shift?.shiftLength ?? 0) / 1000 / 60 / 60),
                  },
                ),
                type: capitalize(shiftType),
                qualifications: shift?.qualifications?.map((qual) => qual.abbreviation).join(', '),
              }}
            />
          </Typography>

          <Box mt={2}>
            <FormAutocomplete
              name="talent"
              fullWidth
              options={talentsOptions}
              groupBy={(option) => {
                const talent = talentsOptions.find((t) => t.id === option.value);
                return intl.formatMessage({ id: `talents.selector.stuffingType.${talent?.groupBy ?? 'Internal'}` });
              }}
              placeholder={intl.formatMessage({ id: 'shifts.assignDialog.placeholder' })}
              required
              async
              onInput={handleAssigneeChange}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  <Stack direction={'row'} alignItems="center" width="100%" spacing={1}>
                    <Box width={'20px'}>
                      {option.agency && (
                        <Tooltip title={option.agency.name} placement="right">
                          <OrganizationAvatar
                            src={option.agency.logoUrl}
                            fullName={option.agency.name}
                            sx={{ width: '20px', height: '20px' }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                    <Box width={'16px'}>
                      {option.isOriented && (
                        <FaIcon
                          name="square-check"
                          color="#4caf50"
                          variant="solid"
                          style={{
                            fontSize: '14px',
                            backgroundColor: '#fff',
                            width: '16px',
                            height: '16px',
                          }}
                        />
                      )}
                    </Box>
                    <Typography>{option.label}</Typography>
                    <Box flex={1} />
                    <Typography variant="caption" color="text.secondary">
                      {(option.qualifications || []).map((q) => q.abbreviation).join(', ')}
                    </Typography>
                  </Stack>
                </li>
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
          <Box color="text.secondary">
            <Button variant="text" color="inherit" rounded onClick={onCloseClick} size="large">
              <FormattedMessage id="common.cancel" />
            </Button>
          </Box>
          <LoadingButton loading={loading} variant="contained" rounded type="submit" size="large">
            <FormattedMessage id="common.submit" />
          </LoadingButton>
        </DialogActions>
      </Form>
    </>
  );
}

type AssignForm = {
  talent: string;
};
